import closeIcon from "../../assests/images/dashboard/Vector 3.png";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { successToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import {
  ADD_EMPLOYEE,
  UPDATE_EMPLOYEE,
  UPDATE_STORE_USER,
} from "src/constants/mutations";
import eyeIconVisible from "../../assests/images/landing-page/eye-open.png";
import eyeIcon from "../../assests/images/landing-page/visibility_off.png";

const renderSchema = (editMode: boolean, isOwner: boolean) => {
  return Yup.object().shape({
    firstName: editMode
      ? Yup.string()
      : Yup.string().required("Employer first name is required"),
    lastName: editMode
      ? Yup.string()
      : Yup.string().required("Employer last name is required"),
    email: editMode
      ? Yup.string().email("Invalid email")
      : Yup.string().email("Invalid email").required("Email Required"),
    phoneNumber: editMode
      ? Yup.number().nullable()
      : Yup.number().required("Phone number is required"),
    employerName: editMode
      ? Yup.string()
      : Yup.string().required("Employer name is required"),
    roles: editMode || isOwner
      ? Yup.string()
      : Yup.string().required("Roles is required"),
    password: editMode
      ? Yup.string()
      : Yup.string().required("Password is required"),
    pin: editMode
      ? Yup.string()
          .optional()
          .test("pin-format", "PIN must be exactly 4 digits", (v) => !v || (v.length === 4 && /^\d+$/.test(v)))
      : Yup.string()
          .required("PIN is required")
          .length(4, "PIN must be exactly 4 digits")
          .matches(/^\d+$/, "PIN must contain only numbers"),
  });
};
export const CreateEmployeeModal = ({
  onClose,
  editValues,
  getEmployees,
  setEditValues,
}: any) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [pinVisible, setPinVisible] = useState(false);
  const { hasRestaurant, user, setUser } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [loading, setLoading] = useState(false);
  const isOwner = !!editValues?.isOwner;
  const isEditMode = !!editValues?.correlationId;

  const httpOrHttps = window.location.href.split("//")[0];
  const hosteName = httpOrHttps + "//" + window.location.host;

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      employerName: "",
      password: "",
      pin: "",
      roles: [] as string[],
    },
    validationSchema: renderSchema(!!editValues?.correlationId, isOwner),
    onSubmit: () => {},
  });

  const addInvite = async () => {
    setLoading(true);
    try {
      const variables: any = {
        createStoreEmployeeInput: {
          employerStoreCorrelationId: hasRestaurant.correlationId,
          firstName: formik.values.firstName,
          lastName: formik.values.lastName,
          email: formik.values.email,
          phoneNumber: formik.values.phoneNumber.toString(),
          employerName: formik.values.employerName,
          password: formik.values.password,
          pin: formik.values.pin,
          hostName: hosteName,
          roles: [formik.values.roles],
        },
      };
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: ADD_EMPLOYEE,
        variables: variables,
      });
       if ( data?.data?.registerStoreEmployee?.correlationId ) {
        getEmployees();
        successToast("Invitation has been successfully sent!");
        onClose();
        setEditValues({});
      } else {
        throw new Error(data.error || "Failed to send invitation.");
      }
    } catch (error) {
      console.error("Error sending invitation:", error);
    } finally {
      setLoading(false);
    }
  };

  const UpdateEmployee = async () => {
    setLoading(true);
    try {
      const payload: any = {
        employeeCorrelationId: editValues.correlationId,
      };
      // Only include fields that have values (empty strings are treated as not provided)
      if (formik.values.firstName?.trim()) payload.firstName = formik.values.firstName.trim();
      if (formik.values.lastName?.trim()) payload.lastName = formik.values.lastName.trim();
      if (formik.values.email?.trim()) payload.email = formik.values.email.trim();
      if (formik.values.phoneNumber) payload.phoneNumber = formik.values.phoneNumber.toString();
      if (formik.values.employerName?.trim()) payload.employerName = formik.values.employerName.trim();
      if (formik.values.password?.trim()) payload.password = formik.values.password;
      if (formik.values.pin?.trim()) payload.pin = formik.values.pin.trim();
      // Only include roles if they changed
      const originalRole = editValues?.roles?.[0];
      if (formik.values.roles && formik.values.roles !== originalRole) {
        payload.roles = [formik.values.roles];
      }
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_EMPLOYEE,
        variables: { updateStoreEmployeeInput: payload },
      });
      if (data?.data?.updateStoreEmployee) {
        successToast("Employee has been Updated!");
        setEditValues({});
        getEmployees();
        onClose();
      } else {
        throw new Error(data?.errors?.[0]?.message || "Failed to update employee.");
      }
    } catch (error) {
      console.error("Error updating employee:", error);
    } finally {
      setLoading(false);
    }
  };

  const UpdateStoreUser = async () => {
    setLoading(true);
    try {
      const payload: any = {
        correlationId: editValues.correlationId,
      };
      // Only include fields that have values (empty strings are treated as not provided)
      if (formik.values.firstName?.trim()) payload.firstName = formik.values.firstName.trim();
      if (formik.values.lastName?.trim()) payload.lastName = formik.values.lastName.trim();
      if (formik.values.email?.trim()) payload.email = formik.values.email.trim();
      if (formik.values.phoneNumber) payload.phoneNumber = formik.values.phoneNumber?.toString?.() ?? String(formik.values.phoneNumber);
      if (formik.values.employerName?.trim()) payload.employerName = formik.values.employerName.trim();
      if (formik.values.password?.trim()) payload.password = formik.values.password;
      if (formik.values.pin?.trim()) payload.pin = formik.values.pin.trim();
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_USER,
        variables: { updateStoreUserInput: payload },
      });
      if (data?.data?.updateStoreUser) {
        const updated = { ...user, storeUser: { ...user?.storeUser, ...data.data.updateStoreUser } };
        setUser(updated);
        localStorage.setItem("user", JSON.stringify(updated));
        successToast("Restaurant owner has been updated!");
        setEditValues({});
        getEmployees();
        onClose();
      } else {
        throw new Error(data?.errors?.[0]?.message || "Failed to update owner.");
      }
    } catch (error) {
      console.error("Error updating owner:", error);
    } finally {
      setLoading(false);
    }
  };

  const { setFieldValue } = formik;

  useEffect(() => {
    if (editValues?.correlationId) {
      setFieldValue("firstName", editValues?.firstName);
      setFieldValue("lastName", editValues?.lastName);
      setFieldValue("email", editValues?.email);
      setFieldValue("phoneNumber", editValues?.phoneNumber);
      setFieldValue("employerName", editValues?.employerName);
      setFieldValue("password", editValues?.password);
      setFieldValue("pin", editValues?.pin || "");
      setFieldValue(
        "roles",
        editValues?.roles ? editValues.roles[0] : []
      );
    }
  }, [editValues, setFieldValue]);

  const togglePassword = () => {
    setPasswordVisible(!passwordVisible);
  };

  const togglePin = () => {
    setPinVisible(!pinVisible);
  };

  const handleClose = () => {
    setEditValues({});
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditValues({});
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, setEditValues]);

  const title = isOwner
    ? "Edit Restaurant Owner"
    : editValues?.correlationId
      ? "Edit Employee"
      : "Add Employee";

  const fieldClassNoToggle =
    "text-sm text-[#252525] h-10 w-full border border-[#F1F1F2] rounded-lg mt-1 px-3 focus:outline-none focus:ring-[3px] focus:ring-[rgba(253,118,87,0.18)] focus:border-[rgba(253,118,87,0.45)]";
  const fieldClassToggle =
    "text-sm text-[#252525] h-10 w-full border border-[#F1F1F2] rounded-lg px-3 pr-10 focus:outline-none focus:ring-[3px] focus:ring-[rgba(253,118,87,0.18)] focus:border-[rgba(253,118,87,0.45)]";
  const labelClass = "text-[#9B9B9B] text-sm block";

  const modal = (
    <div aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div
        className="fixed inset-0 z-[100] flex min-h-0 bg-black/60 items-center justify-center p-3 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:p-4"
        role="presentation"
      >
        <div className="relative transform rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-[min(560px,calc(100vw-1.5rem))] max-h-[min(90dvh,calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-1.5rem))] flex flex-col min-h-0">
          <div className="flex min-h-0 flex-1 flex-col rounded-lg bg-white overflow-hidden">
            <div className="bg-white px-5 py-5 sm:px-8 sm:py-6 min-w-0 flex flex-col flex-1 min-h-0 overflow-y-auto overscroll-contain">
              <div className="flex justify-between items-center shrink-0 gap-2">
                <h2
                  id="modal-title"
                  className="text-xl font-semibold text-[#252525]"
                >
                  {title}
                </h2>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <img className="w-3 h-3" src={closeIcon} alt="" />
                </button>
              </div>

              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      Employee first name {!isEditMode && <span>*</span>}
                    </label>
                    <input
                      name="firstName"
                      type="text"
                      className={fieldClassNoToggle}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.firstName && formik.errors.firstName ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.firstName}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Employee last name {!isEditMode && <span>*</span>}
                    </label>
                    <input
                      name="lastName"
                      type="text"
                      className={fieldClassNoToggle}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.lastName && formik.errors.lastName ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.lastName}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>
                      Email {!isEditMode && <span>*</span>}
                    </label>
                    <input
                      name="email"
                      type="email"
                      className={fieldClassNoToggle}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.email && formik.errors.email ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.email}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Phone number {!isEditMode && <span>*</span>}
                    </label>
                    <input
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className={fieldClassNoToggle}
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.phoneNumber}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <label className={labelClass}>
                      Employer name {!isEditMode && <span>*</span>}
                    </label>
                    <input
                      name="employerName"
                      type="text"
                      className={fieldClassNoToggle}
                      value={formik.values.employerName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.employerName && formik.errors.employerName ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.employerName}
                      </div>
                    ) : null}
                  </div>
                  {!isOwner && (
                    <div>
                      <label className={labelClass}>
                        Roles {!isEditMode && <span>*</span>}
                      </label>
                      <select
                        name="roles"
                        className={`${fieldClassNoToggle} bg-white`}
                        value={formik.values.roles}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      >
                        <option value="">Select</option>
                        <option value="ROLE_EMPLOYEE">Employee</option>
                        <option value="ROLE_MANAGER">Manager</option>
                      </select>
                      {formik.touched.roles && formik.errors.roles ? (
                        <div className="text-orange text-xs mt-1">
                          {formik.errors.roles}
                        </div>
                      ) : null}
                    </div>
                  )}

                  <div>
                    <label className={labelClass}>
                      Password {!isEditMode && <span>*</span>}
                    </label>
                    <div className="relative mt-1">
                      <input
                        name="password"
                        type={passwordVisible ? "text" : "password"}
                        className={fieldClassToggle}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <button
                        type="button"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                        onClick={togglePassword}
                        aria-label={passwordVisible ? "Hide password" : "Show password"}
                      >
                        <img
                          className="w-4 h-4"
                          src={passwordVisible ? eyeIconVisible : eyeIcon}
                          alt=""
                        />
                      </button>
                    </div>
                    {formik.touched.password && formik.errors.password ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.password}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className={labelClass}>
                      PIN {!isEditMode && <span>*</span>}
                    </label>
                    <div className="relative mt-1">
                      <input
                        name="pin"
                        type={pinVisible ? "text" : "password"}
                        className={fieldClassToggle}
                        value={formik.values.pin}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        maxLength={4}
                      />
                      <button
                        type="button"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-md hover:bg-gray-100"
                        onClick={togglePin}
                        aria-label={pinVisible ? "Hide PIN" : "Show PIN"}
                      >
                        <img
                          className="w-4 h-4"
                          src={pinVisible ? eyeIconVisible : eyeIcon}
                          alt=""
                        />
                      </button>
                    </div>
                    {formik.touched.pin && formik.errors.pin ? (
                      <div className="text-orange text-xs mt-1">
                        {formik.errors.pin}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 shrink-0">
                <button
                  type="button"
                  className="px-4 text-sm font-medium text-gray-700 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 focus:outline-none"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                {loading ? (
                  <BtnLoader />
                ) : (
                  <button
                    type="button"
                    className={`px-6 text-sm font-medium text-white h-9 rounded-lg bg-orange hover:bg-orange/90 focus:outline-none ${
                      !formik.isValid ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={
                      isOwner
                        ? UpdateStoreUser
                        : editValues?.correlationId
                          ? UpdateEmployee
                          : addInvite
                    }
                    disabled={!formik.isValid}
                  >
                    {editValues?.correlationId || isOwner ? "Update" : "Add Employee"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};
